import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Form, Input, Button, Typography, Alert } from 'antd';
import PropTypes from 'prop-types';
import '../common.css';
import { jwtDecode } from "jwt-decode";


const { Title, Text } = Typography;

function SignIn({ setUserData }) {
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn } = useAuth(); 
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const handleSignIn = async (values) => {
    setEmailError('');
    setPasswordError('');
    setLoading(true);

    try {
      const { email, password } = values;
      const deviceName = navigator.userAgent;

      const response = await fetch(`${API_URL}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password, 
          deviceName 
        }),
      });

   if (response.ok) {
  const responseData = await response.json();
  const { accessToken, refreshToken, userId, role, isTempPassword } = responseData;

  localStorage.setItem('token', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('userId', userId);
  localStorage.setItem('role', role);

  // 🔍 Decode token to check branch_id
  try {
    const decoded = jwtDecode(accessToken);
    // Store branch_id for later use if you want
    if (decoded.branch_id) {
      localStorage.setItem('branchId', decoded.branch_id);
    }
  } catch (err) {
    console.error("Failed to decode JWT:", err);
  }

  signIn(accessToken);
  setUserData({ userId, role });

  if (isTempPassword) {
    navigate('/change-password');
  } else {
    navigate('/dashboard');
  }
}
 else {
        const errorResponse = await response.json();
        switch (errorResponse.error) {
          case 'UserNotFound':
            setEmailError('The email address is not registered.');
            break;
          case 'InvalidPassword':
            setPasswordError('The password is incorrect.');
            break;
          default:
            setEmailError('Email or password is incorrect.');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setEmailError('Email or password is incorrect.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      height: '100vh',
      background: 'linear-gradient(135deg, #7DDCC0 0%, #F4F8BB 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative',
      overflow: 'hidden'
    }}>
      
      {/* Decorative Elements */}
      <div style={{
        position: 'absolute',
        top: '5%',
        left: '5%',
        width: '150px',
        height: '150px',
        background: 'rgba(125, 220, 192, 0.1)',
        borderRadius: '50%',
        filter: 'blur(40px)',
        animation: 'float 6s ease-in-out infinite'
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '5%',
        right: '5%',
        width: '120px',
        height: '120px',
        background: 'rgba(244, 248, 187, 0.2)',
        borderRadius: '50%',
        filter: 'blur(30px)',
        animation: 'float 8s ease-in-out infinite reverse'
      }} />

      {/* Main Card */}
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        boxShadow: `
          0 20px 40px rgba(125, 220, 192, 0.15),
          0 8px 25px rgba(0, 0, 0, 0.08),
          inset 0 1px 0 rgba(255, 255, 255, 0.9)
        `,
        padding: '32px',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        position: 'relative',
        zIndex: 1
      }}>

        {/* Logo & Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 20px',
            background: 'linear-gradient(135deg, #7DDCC0 0%, #A8E6CF 50%, #F4F8BB 100%)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(125, 220, 192, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              inset: '2px',
              background: 'white',
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src="/images/keaes_hd.png" 
                alt="KEAES Logo" 
                style={{ 
                  width: '75%',
                  height: '75%',
                  objectFit: 'contain',
                  filter: 'contrast(1.1) saturate(1.1)'
                }}
              />
            </div>
          </div>
          
          <Title level={2} style={{ 
            color: '#2d3748',
            marginBottom: 8, 
            fontWeight: 700,
            fontSize: '24px',
            letterSpacing: '-0.025em',
            background: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Welcome Back
          </Title>
          
          <Text style={{ 
            color: '#718096', 
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.4
          }}>
            Sign in to access your learning dashboard
          </Text>
        </div>

        {/* Sign In Form */}
        <Form layout="vertical" onFinish={handleSignIn} requiredMark={false}>
          <Form.Item
            name="email"
            label={
              <span style={{ 
                fontWeight: '600', 
                fontSize: '15px',
                color: '#2d3748',
                letterSpacing: '0.01em'
              }}>
                Email Address
              </span>
            }
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Enter a valid email address.' }
            ]}
            style={{ marginBottom: '24px' }}
          >
            <Input
              size="large"
              placeholder="Enter your email address"
              style={{
                borderRadius: '16px',
                border: '2px solid #e2e8f0',
                background: '#ffffff',
                fontSize: '16px',
                padding: '16px 20px',
                height: '56px',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(125, 220, 192, 0.08)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#7DDCC0';
                e.target.style.boxShadow = '0 0 0 4px rgba(125, 220, 192, 0.12), 0 4px 16px rgba(125, 220, 192, 0.15)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = '0 2px 8px rgba(125, 220, 192, 0.08)';
                e.target.style.transform = 'translateY(0)';
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={
              <span style={{ 
                fontWeight: '600', 
                fontSize: '15px',
                color: '#2d3748',
                letterSpacing: '0.01em'
              }}>
                Password
              </span>
            }
            rules={[{ required: true, message: 'Please input your password!' }]}
            style={{ marginBottom: '32px' }}
          >
            <Input.Password
              size="large"
              placeholder="Enter your password"
              style={{
                borderRadius: '16px',
                border: '2px solid #e2e8f0',
                background: '#ffffff',
                fontSize: '16px',
                padding: '16px 20px',
                height: '56px',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(125, 220, 192, 0.08)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#7DDCC0';
                e.target.style.boxShadow = '0 0 0 4px rgba(125, 220, 192, 0.12), 0 4px 16px rgba(125, 220, 192, 0.15)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = '0 2px 8px rgba(125, 220, 192, 0.08)';
                e.target.style.transform = 'translateY(0)';
              }}
            />
          </Form.Item>

          {emailError && (
            <Alert
              message={emailError}
              type="error"
              showIcon
              style={{
                marginBottom: '24px',
                borderRadius: '16px',
                background: '#fef2f2',
                borderColor: '#fecaca',
                color: '#dc2626',
                fontSize: '15px',
                padding: '12px 16px'
              }}
            />
          )}
          
          {passwordError && (
            <Alert
              message={passwordError}
              type="error"
              showIcon
              style={{
                marginBottom: '24px',
                borderRadius: '16px',
                background: '#fef2f2',
                borderColor: '#fecaca',
                color: '#dc2626',
                fontSize: '15px',
                padding: '12px 16px'
              }}
            />
          )}
<Form.Item style={{ marginBottom: '16px' }}>
  <a
    style={{ fontSize: '14px', color: '#5A9D8A', fontWeight: 500 }}
    onClick={() => navigate('/forgot-password')}
  >
    Forgot Password?
  </a>
</Form.Item>

          <div style={{
            textAlign: 'center',
            fontSize: '15px',
            color: '#718096',
            background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.05) 0%, rgba(244, 248, 187, 0.05) 100%)',
            padding: '20px 24px',
            borderRadius: '16px',
            border: '1px solid rgba(125, 220, 192, 0.1)',
            marginBottom: '40px',
            lineHeight: '1.6'
          }}>
            <div style={{ 
              fontWeight: '600', 
              color: '#4a5568',
              marginBottom: '4px'
            }}>
              Need assistance?
            </div>
            Contact your administrator for support
          </div>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: '40px',
                borderRadius: '16px',
                fontWeight: '700',
                fontSize: '18px',
                background: 'linear-gradient(135deg, #7DDCC0 0%, #6BB5A5 50%, #5A9D8A 100%)',
                border: 'none',
                boxShadow: '0 8px 24px rgba(125, 220, 192, 0.3)',
                transition: 'all 0.3s ease',
                letterSpacing: '0.02em',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 16px 40px rgba(125, 220, 192, 0.4)';
                  e.target.style.background = 'linear-gradient(135deg, #6BB5A5 0%, #5A9D8A 50%, #4A8570 100%)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 24px rgba(125, 220, 192, 0.3)';
                  e.target.style.background = 'linear-gradient(135deg, #7DDCC0 0%, #6BB5A5 50%, #5A9D8A 100%)';
                }
              }}
            >
              <span style={{ 
                fontWeight: '500',
                letterSpacing: '0.05em'
              }}>
                {loading ? 'Signing In...' : 'Sign In'}
              </span>
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}

SignIn.propTypes = {
  setUserData: PropTypes.func.isRequired,
};

export default SignIn;